// src/Components/Header/Header.jsx

import React, { useEffect, useState } from 'react';
import styles from './Header.module.css';
import { HashLink as Link } from 'react-router-hash-link';
import { Link as RouterLink } from 'react-router-dom';
import useServerQuery from '../FetchServerStats';
import { useAuth } from '../../hooks/useAuth';
import { RATING_CONFIG } from '../../config';
import ConsentModal from '../Auth/ConsentModal';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleMenu = () => setIsOpen(!isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const closeOnEscape = (event) => { if (event.key === 'Escape') setIsOpen(false); };
    const desktop = window.matchMedia('(min-width: 1281px)');
    const closeOnDesktop = (event) => { if (event.matches) setIsOpen(false); };
    document.addEventListener('keydown', closeOnEscape);
    desktop.addEventListener('change', closeOnDesktop);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', closeOnEscape);
      desktop.removeEventListener('change', closeOnDesktop);
    };
  }, [isOpen]);

  // Счётчик игроков в шапке (как у majestic-rp)
  const { fetchData, data } = useServerQuery();
  useEffect(() => { fetchData(); }, [fetchData]);
  const playersCount = data?.Universe?.CurrentPlayers ?? 0;

  // Авторизация «Sign in with Hytale»
  const { user, loading: authLoading, login, logout } = useAuth();
  const [consentOpen, setConsentOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const openConsent = () => {
    setIsOpen(false);
    setConsentOpen(true);
  };
  const handleConsentContinue = () => {
    setConsentOpen(false);
    login();
  };
  const handleLogout = async () => {
    setProfileMenuOpen(false);
    setIsOpen(false);
    await logout();
  };

  // Закрытие выпадающего меню профиля по клику вне его.
  useEffect(() => {
    if (!profileMenuOpen) return;
    const onDocClick = (e) => {
      const wrap = document.getElementById('profile-menu-wrap');
      if (wrap && !wrap.contains(e.target)) setProfileMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [profileMenuOpen]);
  return (
    <header className={styles.header}>
      <div className={styles.nav}>
        
        {/* Бургер для мобилок */}
        <button type="button" className={`${styles.burger} ${isOpen ? styles.burgerActive : ''}`} onClick={toggleMenu}
          aria-label={isOpen ? 'Закрыть меню' : 'Открыть меню'} aria-expanded={isOpen} aria-controls="site-navigation">
           <span></span><span></span><span></span>
        </button>

        <div id="site-navigation" className={`${styles.menuPanel} ${isOpen ? styles.menuPanelOpen : ''}`}>
          {/* На узких экранах навигация и действия находятся в одной панели. */}
          <nav className={styles.menu}>
            <Link to="/#about" className={styles.navLink} onClick={() => setIsOpen(false)}>Главная</Link>
            <Link to="/donate#buy-status" className={styles.navLink} onClick={() => setIsOpen(false)}>
              Магазин
            </Link>
            <RouterLink to="/rating" className={styles.navLink} onClick={() => setIsOpen(false)}>
              Рейтинг
            </RouterLink>
            <RouterLink to="/tournaments" className={styles.navLink} onClick={() => setIsOpen(false)}>
              Турниры
            </RouterLink>
            <RouterLink to="/gallery" className={styles.navLink} onClick={() => setIsOpen(false)}>Галерея</RouterLink>
            <RouterLink to="/wiki" className={styles.navLink} onClick={() => setIsOpen(false)}>
              Вики
            </RouterLink>
          </nav>

          {/* Соцсети, начало игры и вход */}
          <div className={styles.actions}>
            <div className={styles.socialsGroup}>
              <a href="https://discord.gg/7CVSZpfYNp" className={styles.socialLink} target="_blank" rel="noreferrer">
                <img src="/discord.png" alt="Discord" className={styles.socialIcon} />
                <span className={styles.mobileSocialLabel} aria-hidden="true">Discord</span>
              </a>
              <a href="https://t.me/hytale_interworld" className={styles.socialLink} target="_blank" rel="noreferrer">
                <img src="/telegram.png" alt="Telegram" className={styles.socialIcon} />
                <span className={styles.mobileSocialLabel} aria-hidden="true">Telegram</span>
              </a>
            </div>
            <a href="/getting-started" className={`btn-primary-custom ${styles.playButton}`}>Играть</a>
            {!authLoading && (
              user ? (
                <div id="profile-menu-wrap" className={styles.profileMenuWrap}>
                  <button
                    type="button"
                    className={styles.profileBtn}
                    onClick={() => setProfileMenuOpen((open) => !open)}
                    aria-expanded={profileMenuOpen}
                    aria-haspopup="menu"
                  >
                    <span className={styles.profileAvatar} aria-hidden="true">
                      <span className={styles.profileAvatarFallback}>
                        {(user.username || '?').charAt(0).toUpperCase()}
                      </span>
                      {user.username && (
                        <img
                          className={styles.profileAvatarImg}
                          src={`${RATING_CONFIG.avatarUrl}/${encodeURIComponent(user.username)}`}
                          alt=""
                          onError={(event) => { event.currentTarget.style.display = 'none'; }}
                        />
                      )}
                    </span>
                    <span className={styles.profileName}>{user.username || 'Профиль'}</span>
                    <span className={`${styles.profileCaret} ${profileMenuOpen ? styles.profileCaretOpen : ''}`} aria-hidden="true">
                      ▼
                    </span>
                  </button>
                  {profileMenuOpen && (
                    <div className={styles.profileMenu} role="menu">
                      <RouterLink
                        to="/profile"
                        className={styles.profileMenuItem}
                        role="menuitem"
                        onClick={() => { setProfileMenuOpen(false); setIsOpen(false); }}
                      >
                        Личный кабинет
                      </RouterLink>
                      <button
                        type="button"
                        className={`${styles.profileMenuItem} ${styles.profileMenuLogout}`}
                        role="menuitem"
                        onClick={handleLogout}
                      >
                        Выйти
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button type="button" className={styles.loginBtn} onClick={openConsent}>
                  Войти
                </button>
              )
            )}
          </div>
        </div>

        {/* Логотип + счётчик игроков рядом (как у majestic-rp) */}
        <div className={styles.brandWrap}>
          <Link to="/#main" className={styles.brand} onClick={() => setIsOpen(false)}>
            <picture>
              <source srcSet="/newlogo.png" media="(max-width: 768px)" />
              <img src="/newlogo.png" alt="INTERWORLD" className={styles.brandImage} />
            </picture>
          </Link>
          {data && (
            <span className={styles.playersCount} title="Игроков онлайн" aria-label={`Игроков онлайн: ${playersCount}`}>
              <span className={styles.playersCountDot} />
              <span className={styles.playersLabel}>Игроков:</span> <b>{playersCount}</b>
            </span>
          )}
        </div>
      </div>
      {isOpen && <div className={styles.overlay} onClick={toggleMenu}></div>}
      <ConsentModal
        open={consentOpen}
        onContinue={handleConsentContinue}
        onCancel={() => setConsentOpen(false)}
      />
    </header>
  );
};

export default Header;
